import React, { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import { downloadDocumentationPdf } from '../utils/documentationPdf';

type DocKind = 'portal' | 'app';

type DocSection = {
  id: string;
  title: string;
  markdown: string;
};

const DOC_SOURCES: Record<DocKind, { file: string; label: string; subtitle: string }> = {
  portal: {
    file: '/BENUTZERDOKUMENTATION_PORTAL.md',
    label: 'Portal',
    subtitle: 'Verwaltung, Dashboard, Admin und Berichte im Webportal.',
  },
  app: {
    file: '/BENUTZERDOKUMENTATION_APP.md',
    label: 'App',
    subtitle: 'Einsatz-App (PWA): Trupps, Offline, NFC und Alarme.',
  },
};

const slugify = (value: string) =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

const parseSections = (markdown: string): DocSection[] => {
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  const sections: DocSection[] = [];
  let current: DocSection | null = null;

  const pushCurrent = () => {
    if (!current) return;
    const body = current.markdown.trim();
    if (body) sections.push({ ...current, markdown: body });
  };

  for (const line of lines) {
    const h2 = /^##\s+(.+)\s*$/.exec(line);
    if (h2) {
      pushCurrent();
      const title = h2[1].trim();
      current = { id: slugify(title), title, markdown: '' };
      continue;
    }
    if (!current) {
      if (/^#\s+/.test(line) || line.trim() === '---' || line.trim() === '') continue;
      continue;
    }
    current.markdown += `${line}\n`;
  }
  pushCurrent();
  return sections;
};

const Documentation: React.FC = () => {
  const { user } = useAuth();
  const [docKind, setDocKind] = useState<DocKind>('portal');
  const [markdownContent, setMarkdownContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeId, setActiveId] = useState('');
  const [pdfBusy, setPdfBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const loadMarkdown = async () => {
      try {
        setLoading(true);
        setActiveId('');
        const response = await fetch(DOC_SOURCES[docKind].file);
        if (!response.ok) {
          throw new Error(`Dokumentation konnte nicht geladen werden (Status: ${response.status})`);
        }
        const text = await response.text();
        if (!cancelled) {
          setMarkdownContent(text);
          setError(null);
        }
      } catch (err) {
        console.error('Error loading documentation:', err);
        if (!cancelled) {
          setError(
            `Die Dokumentation konnte nicht geladen werden: ${
              err instanceof Error ? err.message : 'Unbekannter Fehler'
            }`
          );
          setMarkdownContent('');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void loadMarkdown();
    return () => {
      cancelled = true;
    };
  }, [docKind]);

  const sections = useMemo(() => parseSections(markdownContent), [markdownContent]);

  useEffect(() => {
    if (sections.length && !activeId) setActiveId(sections[0].id);
  }, [sections, activeId]);

  useEffect(() => {
    if (!sections.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible?.target?.id) setActiveId(visible.target.id);
      },
      { rootMargin: '-20% 0px -65% 0px', threshold: [0.1, 0.35, 0.6] }
    );
    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [sections]);

  const scrollToSection = (id: string) => {
    setActiveId(id);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handlePdfExport = () => {
    if (!markdownContent || pdfBusy) return;
    try {
      setPdfBusy(true);
      downloadDocumentationPdf(markdownContent, docKind);
    } catch (err) {
      console.error('PDF export failed:', err);
      alert('PDF konnte nicht erstellt werden.');
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <div className="portal-stack">
      <div className="portal-page-header">
        <div>
          <h1 className="portal-page-title">Benutzerdokumentation</h1>
          <p className="portal-page-subtitle">
            Hallo {user?.firstname || 'Benutzer'} — {DOC_SOURCES[docKind].subtitle}
          </p>
        </div>
        <div className="portal-btn-group">
          <div className="portal-seg">
            <button
              type="button"
              className={`portal-seg-btn${docKind === 'portal' ? ' is-active' : ''}`}
              onClick={() => setDocKind('portal')}
            >
              Portal
            </button>
            <button
              type="button"
              className={`portal-seg-btn${docKind === 'app' ? ' is-active' : ''}`}
              onClick={() => setDocKind('app')}
            >
              App
            </button>
          </div>
          <button
            type="button"
            className="portal-btn portal-btn--primary"
            onClick={handlePdfExport}
            disabled={loading || !!error || !markdownContent || pdfBusy}
            title="Aktuelle Anleitung als PDF herunterladen"
          >
            <FontAwesomeIcon icon={faFilePdf} />
            {pdfBusy ? 'PDF…' : 'PDF exportieren'}
          </button>
        </div>
      </div>

      {loading && <div className="portal-loading">Dokumentation wird geladen…</div>}

      {!loading && error && (
        <div className="portal-card" style={{ maxWidth: 480, margin: '0 auto', textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>⚠️</div>
          <h2 className="portal-card-title">Fehler beim Laden</h2>
          <p className="portal-card-desc">{error}</p>
        </div>
      )}

      {!loading && !error && (
        <>
          <div className="portal-docs-mobile-nav">
            <label className="portal-label" htmlFor="docs-section-select">Kapitel</label>
            <select
              id="docs-section-select"
              className="portal-select"
              value={activeId}
              onChange={(e) => scrollToSection(e.target.value)}
            >
              {sections.map((section) => (
                <option key={section.id} value={section.id}>{section.title}</option>
              ))}
            </select>
          </div>

          <div className="portal-docs-layout">
            <aside className="portal-docs-toc" aria-label="Inhaltsverzeichnis">
              <div className="portal-docs-toc-title">Inhalt · {DOC_SOURCES[docKind].label}</div>
              {sections.map((section) => (
                <button
                  key={section.id}
                  type="button"
                  className={`portal-docs-toc-link${activeId === section.id ? ' is-active' : ''}`}
                  onClick={() => scrollToSection(section.id)}
                >
                  {section.title}
                </button>
              ))}
            </aside>

            <div className="portal-stack" style={{ gap: 16 }}>
              {sections.map((section) => (
                <section
                  key={`${docKind}-${section.id}`}
                  id={section.id}
                  className="portal-card portal-docs-section"
                  style={{ padding: '20px 22px' }}
                >
                  <div className="portal-docs-body">
                    <h2>{section.title}</h2>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {section.markdown}
                    </ReactMarkdown>
                  </div>
                </section>
              ))}

              <div className="portal-hint" style={{ textAlign: 'center', paddingBottom: 8 }}>
                Stand: November 2026 · Bei Fragen: support@as-monitor.ch
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Documentation;
